import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

/**
 * The responsive data table.
 *
 * The brief asks that operator tables "degrade sensibly on narrow screens, not just overflow",
 * and this is how: each column declares a `priority`. On a phone the table stops being a table
 * and becomes a list of cards — the `primary` column becomes the card title, `secondary` columns
 * become a labelled key/value grid, and `detail` columns are dropped entirely rather than
 * squeezed into two characters of width. On a tablet and up it is a real `<table>` with sticky
 * headers.
 *
 * Both renderings come from one column definition, so a column can never exist in one breakpoint
 * and be forgotten in the other.
 *
 * `onRowClick` is a **mouse affordance only** — the row is not given `tabIndex`, because a
 * focusable `<tr>` wrapping a button or a link is a nested interactive control and fails WCAG
 * 4.1.2 (axe's `nested-interactive`). Every table that opts into row clicking therefore also
 * exposes a real focusable control inside the row, which is what keyboard and screen-reader users
 * navigate with. Convenience for the mouse must not become the only path for everyone else.
 */

/** A column definition. */
export interface Column<Row> {
  /** Stable key. */
  id: string;
  /** Column header. Pass `''` for an actions column; a visually hidden label is supplied. */
  header: ReactNode;
  /** Screen-reader name for a column whose visible header is intentionally empty. */
  srHeader?: string | undefined;
  /** Cell renderer. */
  cell: (row: Row) => ReactNode;
  /**
   * How important this column is on a narrow screen:
   * `primary` (the card title), `secondary` (shown as a labelled pair), `detail` (dropped).
   */
  priority?: 'primary' | 'secondary' | 'detail' | undefined;
  /** Extra classes for both header and cell. */
  className?: string | undefined;
  /** Align cell content to the right — used for amounts and counts. */
  align?: 'left' | 'right' | undefined;
  /**
   * Wire sort field this column orders by, e.g. `AMOUNT`. When set and the table receives a
   * `sort` prop, the header becomes a button: click to sort by this field, click again to flip
   * the direction. Card view has no headers, so pages pair this with a `SortControl` below `md`.
   */
  sortKey?: string | undefined;
}

/** The table's sort state plus the header-click handler — see {@link Column.sortKey}. */
export interface TableSort {
  /** The active sort field. */
  key: string;
  /** The active direction. */
  dir: 'ASC' | 'DESC';
  /** Called with the clicked column's `sortKey`; the page decides the new field/direction. */
  onChange: (key: string) => void;
}

/** Props for {@link DataTable}. */
export interface DataTableProps<Row> {
  /** Accessible name for the table. */
  caption: string;
  /** Column definitions. */
  columns: readonly Column<Row>[];
  /** Rows. */
  rows: readonly Row[];
  /** Stable key per row. */
  rowKey: (row: Row) => string;
  /** Called when a row is activated by click, Enter or Space. */
  onRowClick?: ((row: Row) => void) | undefined;
  /** Marks the currently selected row. */
  isRowSelected?: ((row: Row) => boolean) | undefined;
  /** Rendered instead of the table when `rows` is empty. */
  empty?: ReactNode | undefined;
  /** Sort state; enables header sorting on columns that declare a `sortKey`. */
  sort?: TableSort | undefined;
  /** Additional classes. */
  className?: string | undefined;
}

/** A table that becomes a card list below the `md` breakpoint. */
export function DataTable<Row>({
  caption,
  columns,
  rows,
  rowKey,
  onRowClick,
  isRowSelected,
  empty,
  sort,
  className,
}: DataTableProps<Row>): ReactNode {
  const { t } = useTranslation();
  if (rows.length === 0 && empty) return <>{empty}</>;

  const primary = columns.find((c) => c.priority === 'primary') ?? columns[0];
  const cardColumns = columns.filter((c) => c.priority !== 'detail' && c.id !== primary?.id);

  return (
    <div className={cn('w-full', className)}>
      {/* Table, md and up */}
      <div className="hidden overflow-x-auto rounded-lg border border-border md:block">
        <table className="w-full min-w-full caption-bottom text-sm">
          <caption className="sr-only">{caption}</caption>
          <thead className="sticky top-0 z-10 bg-muted/60 backdrop-blur">
            <tr className="border-b border-border">
              {columns.map((column) => {
                const sortable = sort !== undefined && column.sortKey !== undefined;
                const active = sortable && sort.key === column.sortKey;
                return (
                  <th
                    key={column.id}
                    scope="col"
                    aria-sort={
                      active ? (sort.dir === 'ASC' ? 'ascending' : 'descending') : undefined
                    }
                    className={cn(
                      'whitespace-nowrap px-4 py-2.5 text-2xs font-semibold uppercase tracking-wide text-muted-foreground',
                      column.align === 'right' ? 'text-right' : 'text-left',
                      column.className,
                    )}
                  >
                    {column.header === '' || column.header === undefined ? (
                      <span className="sr-only">
                        {column.srHeader ?? t('layout.dataTable.actionsFallback')}
                      </span>
                    ) : sortable ? (
                      <button
                        type="button"
                        onClick={() => {
                          sort.onChange(column.sortKey ?? '');
                        }}
                        className={cn(
                          '-mx-1 inline-flex items-center gap-1 rounded px-1 py-0.5 uppercase tracking-wide transition-colors',
                          'hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                          active && 'text-foreground',
                        )}
                      >
                        {column.header}
                        {active ? (
                          sort.dir === 'ASC' ? (
                            <ArrowUp className="size-3" aria-hidden="true" />
                          ) : (
                            <ArrowDown className="size-3" aria-hidden="true" />
                          )
                        ) : (
                          <ChevronsUpDown className="size-3 opacity-50" aria-hidden="true" />
                        )}
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((row) => {
              const selected = isRowSelected?.(row) ?? false;
              return (
                <tr
                  key={rowKey(row)}
                  data-selected={isRowSelected ? selected : undefined}
                  onClick={
                    onRowClick
                      ? () => {
                          onRowClick(row);
                        }
                      : undefined
                  }
                  className={cn(
                    'transition-colors',
                    onRowClick && 'cursor-pointer hover:bg-muted/50',
                    selected && 'bg-accent/60',
                  )}
                >
                  {columns.map((column) => (
                    <td
                      key={column.id}
                      className={cn(
                        'px-4 py-3 align-middle',
                        column.align === 'right' ? 'text-right' : 'text-left',
                        column.className,
                      )}
                    >
                      {column.cell(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Cards, below md */}
      <ul className="space-y-2 md:hidden" aria-label={caption}>
        {rows.map((row) => {
          const selected = isRowSelected?.(row) ?? false;
          return (
            <li key={rowKey(row)}>
              <div
                onClick={
                  onRowClick
                    ? () => {
                        onRowClick(row);
                      }
                    : undefined
                }
                className={cn(
                  'w-full space-y-3 rounded-lg border border-border bg-card p-4 text-left',
                  onRowClick && 'cursor-pointer transition-colors hover:bg-muted/40',
                  selected && 'border-primary/40 bg-accent/50',
                )}
              >
                {primary ? <div className="min-w-0">{primary.cell(row)}</div> : null}
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {cardColumns.map((column) => (
                    <div key={column.id} className="min-w-0 space-y-0.5">
                      <dt className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
                        {column.header === '' || column.header === undefined
                          ? (column.srHeader ?? t('layout.dataTable.actionsFallback'))
                          : column.header}
                      </dt>
                      <dd className="truncate text-sm">{column.cell(row)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
